// src/components/ui/PriorityBadge.jsx
import React from 'react';
import { Chip, Box, Tooltip, useTheme, alpha } from '@mui/material';
import {
  KeyboardDoubleArrowUp as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Remove as MediumIcon,
  KeyboardArrowDown as LowIcon
} from '@mui/icons-material';

/**
 * Component to display priority level with appropriate color and icon
 * @param {Object} props Component props
 * @param {string} props.priority Priority level (low, medium, high, urgent)
 * @param {string} props.size Size of the badge (small, medium)
 * @param {boolean} props.iconOnly Whether to show only the icon without text
 * @param {boolean} props.showTooltip Whether to show a tooltip on hover
 */
const PriorityBadge = ({ 
  priority, 
  size = 'medium', 
  iconOnly = false,
  showTooltip = true,
  ...props 
}) => {
  const theme = useTheme();
  
  // Define configuration for different priorities
  const priorityConfig = {
    urgent: {
      label: 'Urgent',
      color: theme.palette.error.main,
      icon: <UrgentIcon fontSize={size} />
    },
    high: {
      label: 'High',
      color: theme.palette.warning.main,
      icon: <HighIcon fontSize={size} />
    },
    medium: {
      label: 'Medium',
      color: theme.palette.info.main,
      icon: <MediumIcon fontSize={size} />
    },
    low: {
      label: 'Low',
      color: theme.palette.success.main,
      icon: <LowIcon fontSize={size} />
    }
  };
  
  // Get config or use default
  const config = priorityConfig[priority] || priorityConfig.medium;
  
  const badge = iconOnly ? (
    <Box 
      sx={{ 
        color: config.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      {...props}
    >
      {config.icon}
    </Box>
  ) : (
    <Chip
      size={size}
      label={config.label}
      icon={config.icon}
      sx={{ 
        bgcolor: alpha(config.color, 0.1),
        color: config.color,
        borderColor: config.color,
        '& .MuiChip-icon': {
          color: config.color,
        }
      }}
      variant="outlined"
      {...props}
    />
  );
  
  if (showTooltip) {
    return (
      <Tooltip title={config.label}>
        {badge}
      </Tooltip>
    );
  }
  
  return badge;
};

export default PriorityBadge;